import { saveWithVersion } from './staleSave'
import { reloadPage } from '@/lib/player/leave'

/**
 * The versions of the settings cards kept in their own row (chat, notices,
 * SMS and WhatsApp — one row for both —, credits, business information,
 * payout, each default e-mail and its languages), David 2026-10-28: the GET
 * and every save answer bring `updatedAt`, the next save sends it back, and a
 * stale save (409) asks between reloading the page and saving over it. A card
 * never read (or a row never saved: null) writes without the check.
 */
const versions = new Map<string, string>()

export const noteRowVersion = (key: string, value: unknown) => {
  if (typeof value === 'string' && value) versions.set(key, value)
}

/** Keeps `updatedAt` of an answer (the row itself, or `{ data }` / `{ settings }` around it). */
export const noteRowFrom = <T,>(key: string, body: T): T => {
  const b = body as { updatedAt?: unknown; settings?: { updatedAt?: unknown } } | null
  noteRowVersion(key, b?.updatedAt ?? b?.settings?.updatedAt)
  return body
}

export const getRowVersion = (key: string): string | undefined => versions.get(key)

/** `If-Unmodified-Since` for a write without a body (DELETE); the server reads whole seconds. */
export const unmodifiedSince = (version: string | null | undefined): Record<string, string> | undefined =>
  (version ? { 'If-Unmodified-Since': new Date(version).toUTCString() } : undefined)

/** A card's save with its version; the answer's version is kept (`pick`, default `updatedAt`). */
export const versionedRowWrite = async <T,>(
  key: string,
  write: (version: string | null | undefined) => Promise<T>,
  pick: (result: T) => unknown = (result) => (result as { updatedAt?: unknown; settings?: { updatedAt?: unknown } } | null)?.updatedAt
    ?? (result as { settings?: { updatedAt?: unknown } } | null)?.settings?.updatedAt,
): Promise<T> => {
  const result = await saveWithVersion(write, { version: versions.get(key), onReload: () => reloadPage() })
  noteRowVersion(key, pick(result))
  return result
}

/** A default e-mail's version key: the override, or one of its languages. */
export const overrideRow = (emailKey: string, language?: string) => `emailOverride:${emailKey}${language ? `:${language}` : ''}`
